import {FileDataModel, UploadResult} from '../../api/types.ts';
import FileFormats, {FileFormat} from '../../lib/FileFormats.ts';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Progress, Spinner} from 'reactstrap';
import {ReactStateSetter} from '../../types.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import {FileRejection, useDropzone} from 'react-dropzone';
import getFilesFromUploadResults from './getFilesFromUploadResults.ts';
import humanFileSize from '../../lib/humanFileSize.ts';
import {MAX_UPLOAD_BYTES} from '../../lib/UploadLimits.ts';
import useClientConfig from '../../hooks/useClientConfig.tsx';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import {
    uploadFileInChunks,
    UploadProgress,
    UploadStage
} from '../../api/uploads/chunkedUpload.ts';

interface UploadFromComputerProps {
    folderPath?: string | null;
    specificContainerName?: string | null;
    // The one file the modal may hand over (a picked Drive photo, a Dropbox image, the image editor's subject).
    selectedFile: File | null;
    setSelectedFile: ReactStateSetter<File | null>;
    onClose: () => void;
    onFilesUploaded: (files: FileDataModel[]) => void;
    backendError: string | null;
    setBackendError: ReactStateSetter<string | null>;
    selectedFormat: FileFormat | null;
    setShowUploadFromComputer: ReactStateSetter<boolean>;
    getEditedImage: () => File | null;
}

type FileState = {
    file: File;
    sentBytes: number;
    stage: UploadStage | 'waiting';
    error?: string;
};

const stageText = (state: FileState) => {
    switch (state.stage) {
        case 'waiting':
            return 'Waiting';
        case 'uploading':
            return `Uploading ${Math.floor((100 * state.sentBytes) / Math.max(1, state.file.size))}%`;
        case 'processing':
            return state.file.name.toLowerCase().endsWith('.zip')
                ? 'Unpacking'
                : 'Processing';
        case 'done':
            return 'Done';
        case 'failed':
            return state.error ?? 'Failed';
    }
};

/*
 * Picking files on this device and uploading them in chunks, one file at a
 * time, with a bar per file and one for the lot. A large file takes a
 * while; the person can watch it, and cancel it.
 */
const UploadFromComputer: React.FC<UploadFromComputerProps> = ({
    folderPath,
    specificContainerName,
    selectedFile,
    setSelectedFile,
    onClose,
    onFilesUploaded,
    backendError,
    setBackendError,
    selectedFormat,
    setShowUploadFromComputer,
    getEditedImage
}) => {
    const {apiServiceBaseUri} = useClientConfig();
    const {getAccessToken} = useAuthStateManager();

    // More than one file picked here; a single pick goes through selectedFile so the image editor can work on it.
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [states, setStates] = useState<FileState[] | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const filesToUpload: File[] = selectedFiles.length > 0
        ? selectedFiles
        : selectedFile
          ? [selectedFile]
          : [];

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) {
                return;
            }
            setBackendError(null);
            setStates(null);
            if (acceptedFiles.length === 1) {
                setSelectedFiles([]);
                setSelectedFile(acceptedFiles[0]);
            } else {
                setSelectedFile(null);
                setSelectedFiles(acceptedFiles);
            }
        },
        [setBackendError, setSelectedFile]
    );

    // The server refuses a bigger file too (UploadLimits); this just says so before the upload starts.
    const onDropRejected = useCallback(
        (rejections: FileRejection[]) => {
            const tooLarge = rejections.some(rejection =>
                rejection.errors.some(error => error.code === 'file-too-large')
            );
            setBackendError(
                tooLarge
                    ? `Files can be at most ${humanFileSize(MAX_UPLOAD_BYTES)}.`
                    : (rejections[0]?.errors[0]?.message ?? "That file can't be uploaded.")
            );
        },
        [setBackendError]
    );

    const extensions =
        selectedFormat === null
            ? ['*']
            : FileFormats.getExtensions(selectedFormat, true);

    const validFileTypes =
        selectedFormat !== null
            ? FileFormats.getExtensions(selectedFormat, true)
                  .map(f => `.${f}`)
                  .join(', ')
            : '*.*';

    const acceptedFileTypes =
        extensions[0] === '*' || selectedFormat === null
            ? undefined
            : FileFormats.getAcceptFileTypes(selectedFormat, true);

    const uploading = states !== null && states.some(s => s.stage === 'waiting' || s.stage === 'uploading' || s.stage === 'processing');

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        onDropRejected,
        accept: acceptedFileTypes,
        maxSize: MAX_UPLOAD_BYTES,
        multiple: true,
        disabled: uploading
    });

    const dropzoneClass = filesToUpload.length > 0 ? 'dropzone file-selected' : 'dropzone';

    const renderDropzoneContent = () => {
        if (filesToUpload.length === 1) {
            return <p>File selected: {filesToUpload[0].name}</p>;
        } else if (filesToUpload.length > 1) {
            return <p>{filesToUpload.length} files selected</p>;
        } else if (isDragActive) {
            return <p>Drop the files here ...</p>;
        } else {
            return <p>Drag 'n' drop files here, or click to select files</p>;
        }
    };

    const updateState = (index: number, change: Partial<FileState>) =>
        setStates(current =>
            current ? current.map((s, i) => (i === index ? {...s, ...change} : s)) : current
        );

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const editedImage = getEditedImage();
        const files = editedImage && filesToUpload.length <= 1 ? [editedImage] : filesToUpload;

        if (files.length === 0) {
            return;
        }

        setBackendError(null);
        setStates(files.map(file => ({file, sentBytes: 0, stage: 'waiting'})));

        const abort = new AbortController();
        abortRef.current = abort;
        // A zip answers with every file it unpacked, so results are counted apart from files.
        const uploaded: UploadResult[] = [];
        let failed = 0;
        let firstError: string | null = null;

        try {
            for (let index = 0; index < files.length; index++) {
                const onProgress = (progress: UploadProgress) =>
                    updateState(index, {sentBytes: progress.sentBytes, stage: progress.stage});

                try {
                    uploaded.push(
                        ...(await uploadFileInChunks({
                            file: files[index],
                            containerName: specificContainerName,
                            folderPath,
                            apiServiceBaseUri,
                            getAccessToken,
                            onProgress,
                            signal: abort.signal
                        }))
                    );
                } catch (error) {
                    if (abort.signal.aborted) {
                        throw error;
                    }
                    // One file failing doesn't stop the rest; it is reported on its own row.
                    const message = unknownErrorToString(error);
                    failed++;
                    firstError ??= message;
                    updateState(index, {stage: 'failed', error: message});
                }
            }

            if (abort.signal.aborted) {
                return;
            }

            if (uploaded.length > 0) {
                onFilesUploaded(getFilesFromUploadResults(uploaded, selectedFormat));
            }

            if (failed === 0) {
                onClose();
            } else if (failed < files.length) {
                setBackendError(`${files.length - failed} of ${files.length} files uploaded; ${failed} failed.`);
            } else {
                setBackendError(firstError);
            }
        } catch (error) {
            if (!abort.signal.aborted) {
                setBackendError(unknownErrorToString(error));
            }
        } finally {
            abortRef.current = null;
            setStates(current =>
                current
                    ? current.map(s =>
                          s.stage === 'uploading' || s.stage === 'processing' || s.stage === 'waiting'
                              ? {...s, stage: 'failed', error: 'Cancelled'}
                              : s
                      )
                    : current
            );
        }
    };

    const cancel = () => {
        abortRef.current?.abort();
    };

    const totalBytes = states?.reduce((total, s) => total + s.file.size, 0) ?? 0;
    const sentTotal = states?.reduce((total, s) => total + (s.stage === 'done' ? s.file.size : s.sentBytes), 0) ?? 0;

    return (
        <form onSubmit={handleSubmit}>
            <div {...getRootProps()} className={dropzoneClass}>
                <input {...getInputProps()} />
                <div className="text-center">{renderDropzoneContent()}</div>
            </div>

            <small className="d-block mt-2 text-center">
                {validFileTypes} · up to {humanFileSize(MAX_UPLOAD_BYTES)} each
            </small>
            {backendError && <Alert color="danger mt-3">{backendError}</Alert>}

            {states && (
                <div className="mt-3" aria-live="polite">
                    {states.length > 1 && (
                        <div className="mb-2">
                            <small>
                                {humanFileSize(sentTotal)} of {humanFileSize(totalBytes)}
                            </small>
                            <Progress value={totalBytes ? (100 * sentTotal) / totalBytes : 0} />
                        </div>
                    )}
                    {states.map((s, index) => (
                        <div key={index} className="mb-2">
                            <div className="d-flex justify-content-between">
                                <small className="text-truncate me-2">{s.file.name}</small>
                                <small className={s.stage === 'failed' ? 'text-danger' : 'text-secondary'}>
                                    {stageText(s)}
                                </small>
                            </div>
                            <Progress
                                value={s.stage === 'done' ? 100 : (100 * s.sentBytes) / Math.max(1, s.file.size)}
                                color={s.stage === 'failed' ? 'danger' : s.stage === 'done' ? 'success' : undefined}
                                animated={s.stage === 'processing'}
                                striped={s.stage === 'processing'}
                            />
                        </div>
                    ))}
                </div>
            )}

            {uploading ? (
                <Button color="danger" block className="mt-3" onClick={cancel} type="button">
                    <Spinner size="sm" className="me-2" />
                    Cancel upload
                </Button>
            ) : (
                <Button
                    color="primary"
                    type="submit"
                    disabled={filesToUpload.length === 0}
                    block
                    className="mt-3"
                >
                    {filesToUpload.length > 1 ? `Upload ${filesToUpload.length} files` : 'Upload'}
                </Button>
            )}

            <Button
                color="secondary"
                block
                className="mt-3"
                disabled={uploading}
                onClick={() => setShowUploadFromComputer(false)}
            >
                Cancel
            </Button>
        </form>
    );
};

export default UploadFromComputer;
